import { useMemo, useState } from 'react'
import type { TradedBy } from './api'
import { isEffectivelyBlack, pickReadableText } from './contrast'

// Three-zone carried-by control: WE TRADE / TRADED BY SPORTSBOOK /
// NOT CARRIED. Replaces the binary RelationPicker for the carriage
// relation. Drag a chip into another zone to reclassify it; clicking
// cycles WE TRADE → BOOK TRADES → NOT CARRIED → WE TRADE (the fallback
// for touch screens, where HTML5 drag-and-drop never fires).
//
// Chips render A→Z inside every zone regardless of drop order — position
// within a zone never encodes anything. Chip fill is the entity's brand
// color (auto-contrast text); brands that are missing or effectively
// black fall back to the theme ink fill, which flips with dark mode.

export interface ZoneOption {
  id: number
  divisionId?: number | null
  label: string
  color?: string | null
}

export interface ZoneSelection {
  id: number
  divisionId?: number | null
  tradedBy: TradedBy
}

interface Props {
  label: string
  options: ZoneOption[]
  selected: ZoneSelection[]
  onChange: (next: ZoneSelection[]) => void
  readOnly?: boolean
  /** Hide the NOT CARRIED pool (read-only summary surfaces). */
  showNotCarried?: boolean
}

type ZoneId = TradedBy | 'off'

const CYCLE: Record<ZoneId, ZoneId> = { us: 'book', book: 'off', off: 'us' }

const ZONES: { id: ZoneId, title: string }[] = [
  { id: 'us', title: 'WE TRADE' },
  { id: 'book', title: 'TRADED BY SPORTSBOOK' },
  { id: 'off', title: 'NOT CARRIED' },
]

function keyOf(s: { id: number, divisionId?: number | null }): string {
  return `${s.id}::${s.divisionId ?? ''}`
}

export default function CarrierZones({
  label,
  options,
  selected,
  onChange,
  readOnly = false,
  showNotCarried = true,
}: Props) {
  const [filter, setFilter] = useState('')
  const [draggedKey, setDraggedKey] = useState<string | null>(null)
  const [overZone, setOverZone] = useState<ZoneId | null>(null)

  const stateByKey = useMemo(() => {
    const map = new Map<string, TradedBy>()
    for (const s of selected) map.set(keyOf(s), s.tradedBy)
    return map
  }, [selected])

  const zoned = useMemo(() => {
    const q = filter.trim().toLowerCase()
    const visible = q ? options.filter(o => o.label.toLowerCase().includes(q)) : options
    const byZone: Record<ZoneId, ZoneOption[]> = { us: [], book: [], off: [] }
    for (const opt of visible) {
      byZone[stateByKey.get(keyOf(opt)) ?? 'off'].push(opt)
    }
    for (const zone of Object.values(byZone)) {
      zone.sort((a, b) => a.label.localeCompare(b.label))
    }
    return byZone
  }, [options, stateByKey, filter])

  // Unfiltered counts — the zone headers report the real totals even
  // while a filter narrows what's on screen.
  const totals = useMemo(() => {
    let us = 0
    let book = 0
    for (const opt of options) {
      const state = stateByKey.get(keyOf(opt))
      if (state === 'us') us++
      else if (state === 'book') book++
    }
    return { us, book, off: options.length - us - book }
  }, [options, stateByKey])

  function setZone(opt: ZoneOption, zone: ZoneId) {
    if (readOnly) return
    const k = keyOf(opt)
    const rest = selected.filter(s => keyOf(s) !== k)
    const next = zone === 'off'
      ? rest
      : [...rest, { id: opt.id, divisionId: opt.divisionId ?? null, tradedBy: zone }]
    onChange(next.sort((a, b) => keyOf(a).localeCompare(keyOf(b))))
  }

  function cycle(opt: ZoneOption) {
    setZone(opt, CYCLE[stateByKey.get(keyOf(opt)) ?? 'off'])
  }

  const zones = showNotCarried ? ZONES : ZONES.filter(z => z.id !== 'off')

  return (
    <div className={`ad-zones${readOnly ? ' is-read-only' : ''}`}>
      <div className="ad-grid-head">
        <span className="b-mono">{label}</span>
        <span className="b-mono-dim b-tiny">
          {totals.us + totals.book} / {options.length} CARRIED · {totals.us} ASD-TRADED
        </span>
        {!readOnly && (
          <span className="b-mono-dim b-tiny ad-zones-hint">drag between zones · click cycles</span>
        )}
      </div>
      <input
        type="text"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        placeholder="filter…"
        className="ad-relation-filter"
        spellCheck={false}
      />
      {zones.map((zone) => {
        const chips = zoned[zone.id]
        return (
          <div
            key={zone.id}
            className={`ad-zone${zone.id !== 'off' ? ' is-solid' : ''}${overZone === zone.id && draggedKey ? ' is-drag-over' : ''}`}
            onDragOver={(e) => {
              if (readOnly || !draggedKey) return
              e.preventDefault()
              e.dataTransfer.dropEffect = 'move'
              setOverZone(zone.id)
            }}
            onDragLeave={(e) => {
              if (e.currentTarget.contains(e.relatedTarget as Node)) return
              setOverZone(current => (current === zone.id ? null : current))
            }}
            onDrop={(e) => {
              e.preventDefault()
              setOverZone(null)
              if (readOnly || !draggedKey) return
              const opt = options.find(o => keyOf(o) === draggedKey)
              setDraggedKey(null)
              if (opt) setZone(opt, zone.id)
            }}
          >
            <div className="ad-zone-title">
              {zone.title}
              {' '}
              <span className="ad-zone-count">{totals[zone.id]}</span>
            </div>
            {chips.length === 0
              ? (
                  <p className="ad-zone-empty">
                    {readOnly ? 'none' : filter.trim() ? 'no matches' : 'drop here'}
                  </p>
                )
              : (
                  <div className="ad-zone-cells">
                    {chips.map((opt) => {
                      const k = keyOf(opt)
                      const carried = zone.id !== 'off'
                      let brandBg = carried && opt.color && !isEffectivelyBlack(opt.color) ? opt.color : null
                      const brandFg = brandBg ? pickReadableText(brandBg) : null
                      // Unparseable brand color → fall back to the ink fill.
                      if (!brandFg) brandBg = null
                      return (
                        <button
                          key={k}
                          type="button"
                          disabled={readOnly}
                          draggable={!readOnly}
                          onClick={() => cycle(opt)}
                          onDragStart={(e) => {
                            setDraggedKey(k)
                            e.dataTransfer.setData('text/plain', opt.label)
                            e.dataTransfer.effectAllowed = 'move'
                          }}
                          onDragEnd={() => {
                            setDraggedKey(null)
                            setOverZone(null)
                          }}
                          className={`ad-zone-cell${carried ? (brandBg ? '' : ' is-ink') : ' is-off'}${draggedKey === k ? ' is-dragging' : ''}`}
                          style={brandBg ? { background: brandBg, color: brandFg! } : undefined}
                        >
                          <span className="ad-zone-cell-label">{opt.label}</span>
                        </button>
                      )
                    })}
                  </div>
                )}
          </div>
        )
      })}
    </div>
  )
}
